import { useMemo } from "react";
import type { CSSProperties } from "react";
import { hasCssVariableDeclarations, parseCssVariables } from "../../utils/parseCssVariables";
import { resolveWidgetTheme, type ColourScheme, type WidgetTheme } from "../../utils/widgetTheme";
import { useWidgetColourScheme } from "./useWidgetColourScheme";

/** The class a surface carries to opt into the design (see _design-tokens.scss). */
export const THEMED_SURFACE_CLASS = "gc-themed";

/**
 * Which palette the *light* scheme paints. `design` is the design's own; the
 * widget uses `legacy` because its light rendering was signed off identical to
 * the pre-token palette. Dark always uses the design's tokens.
 */
export type DesignPalette = "design" | "legacy";

export interface ThemedSurfaceOptions {
  /**
   * The surface's `theme` attribute: `light`, `dark` or `auto`. Where it holds
   * `--gc-*` declarations instead it is treated as styling, which is the
   * pre-`custom-styles` spelling some embeds still use.
   */
  readonly theme?: string;
  /**
   * `--gc-*` overrides from the surface's `custom-styles` attribute, applied
   * inline on the surface root.
   *
   * A nested surface must be given them even when an ancestor already carries
   * them: the token map declares `--gc-color-*` on the surface itself, and a
   * declaration on a child replaces an inherited one however the ancestor set
   * it — inline included. Without this the surface would ignore the venue's
   * colours and fonts, and `buildWidgetStripeAppearance` would read the
   * design's values rather than the venue's.
   */
  readonly customStyles?: string;
  /** Defaults to the design's palette; only the widget opts out. */
  readonly palette?: DesignPalette;
}

export interface ThemedSurface {
  /** The resolved setting, for `GuestCheckoutSettings.theme`. */
  readonly widgetTheme: WidgetTheme;
  /** The scheme actually painted, with `auto` resolved against the browser. */
  readonly colourScheme: ColourScheme;
  /** Venue `--gc-*` overrides, to apply inline on the surface root. */
  readonly style?: CSSProperties;
  /**
   * Changes whenever the applied overrides do, for
   * `GuestCheckoutSettings.venueStyleKey`. Surfaces repaint from the variables
   * themselves, but anything reading their *computed* values has to be told to
   * read again — see the Stripe appearance in stripeConnect.tsx.
   */
  readonly venueStyleKey: string;
  /** The marker's attributes, to spread onto the surface root. */
  readonly themeAttributes: {
    readonly "data-gc-colour-scheme": ColourScheme;
    readonly "data-gc-palette": DesignPalette;
  };
}

/**
 * Resolves everything a surface needs to paint the design: the colour scheme
 * (attribute, `?theme=` query, or the browser's preference for `auto`, which
 * re-resolves live), the venue's inline `--gc-*` overrides, and the marker
 * attributes the stylesheets key off.
 *
 * Surfaces whose root is a plain element should use {@link ThemedSurface}
 * instead, which renders the root and the settings provider too; this hook is
 * for roots that carry their own behaviour, like the widget's `<dialog>`.
 */
export function useThemedSurface({ theme, customStyles, palette = "design" }: ThemedSurfaceOptions): ThemedSurface {
  // A `theme` holding `--gc-*` declarations is the pre-`custom-styles`
  // spelling, so it is treated as styling and leaves the scheme at its default.
  const legacyStyles = hasCssVariableDeclarations(theme) ? theme : undefined;
  const schemeAttribute = legacyStyles ? undefined : theme;

  const widgetTheme = useMemo(
    () => resolveWidgetTheme(schemeAttribute, typeof window !== "undefined" ? window.location.search : ""),
    [schemeAttribute],
  );

  const colourScheme = useWidgetColourScheme(widgetTheme);

  // `custom-styles` wins over the legacy spelling where a surface sets both.
  const style = useMemo(() => {
    const variables = { ...parseCssVariables(legacyStyles), ...parseCssVariables(customStyles) };
    return Object.keys(variables).length > 0 ? variables : undefined;
  }, [legacyStyles, customStyles]);

  // Derived from the parsed declarations rather than the raw attributes, so
  // reformatting the same overrides does not repaint the hosted payment fields.
  const venueStyleKey = useMemo(
    () =>
      Object.entries(style ?? {})
        .map(([property, value]) => `${property}:${value}`)
        .sort((a, b) => a.localeCompare(b))
        .join(";"),
    [style],
  );

  const themeAttributes = useMemo(
    () => ({ "data-gc-colour-scheme": colourScheme, "data-gc-palette": palette }) as const,
    [colourScheme, palette],
  );

  return { widgetTheme, colourScheme, style, venueStyleKey, themeAttributes };
}
